/**
 *
 *
 * @depend https://github.com/Hacker0x01/react-datepicker/
 */
import React, { useCallback } from 'react';
import styled from 'styled-components';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { setMinutes, setHours } from 'date-fns';
import { ja } from 'date-fns/locale';
import StyleConfig from 'src/constants/StyleConfig';

registerLocale('ja', ja);

type Props = {
  className?: string;
  date?: Date;
  placeholder?: string;
  dateFormat?: string;
  timeFormat?: string;
  timeInterval?: number;
  onChange?: (date: Date) => void;
  isDisabled?: boolean;
  showTimeSelect?: boolean;
  disabledKeyboardNavigation?: boolean;
  includeTimes?: Date[];
  minDate?: Date;
  maxDate?: Date;
  minTime?: Date;
  maxTime?: Date;
};

// 選択可能な時間を設定
const now = new Date();
now.setHours(4);
now.setMinutes(0);
now.setSeconds(0);

const defaultProps = {
  className: '',
  dateFormat: 'Pp',
  timeFormat: 'p',
  isDisabled: false,
  showTimeSelect: true,
  disabledKeyboardNavigation: false,

  minTime: setMinutes(now, 0),
  maxTime: setHours(setMinutes(now, 30), 23),
};

export const MyDatePicker = ({ ...props }: Props) => {
  const {
    className,
    date,
    placeholder,
    dateFormat,
    timeFormat,
    timeInterval,
    isDisabled,
    showTimeSelect,
    onChange,
    disabledKeyboardNavigation,
    minDate,
    maxDate,
    minTime,
    maxTime,
  } = props;

  const wrapOnChange = useCallback(
    (value) => {
      if (onChange) {
        onChange(value);
      }
    },
    [onChange],
  );

  return (
    <Styled>
      <DatePicker
        className={className}
        selected={date}
        onChange={wrapOnChange}
        locale="ja"
        showTimeSelect={showTimeSelect}
        timeFormat={timeFormat}
        timeIntervals={timeInterval || 15}
        dateFormat={dateFormat}
        placeholderText={placeholder}
        disabled={isDisabled}
        disabledKeyboardNavigation={disabledKeyboardNavigation}
        minDate={minDate}
        maxDate={maxDate}
        minTime={minTime}
        maxTime={maxTime}
      />
    </Styled>
  );
};

MyDatePicker.defaultProps = defaultProps;

export const MyTimePicker = ({ ...props }: Props) => {
  const {
    className,
    date,
    placeholder,
    dateFormat,
    timeFormat,
    timeInterval,
    onChange,
    isDisabled,
    disabledKeyboardNavigation,
    minTime,
    maxTime,
  } = props;

  const wrapOnChange = useCallback(
    (value) => {
      if (onChange) {
        onChange(value);
      }
    },
    [onChange],
  );

  return (
    <Styled>
      <DatePicker
        className={className}
        selected={date}
        onChange={wrapOnChange}
        locale="ja"
        showTimeSelect
        showTimeSelectOnly
        timeFormat={timeFormat}
        timeIntervals={timeInterval || 15}
        timeCaption="Time"
        dateFormat={dateFormat}
        placeholderText={placeholder}
        disabled={isDisabled}
        disabledKeyboardNavigation={disabledKeyboardNavigation}
        minTime={minTime}
        maxTime={maxTime}
      />
    </Styled>
  );
};

MyTimePicker.defaultProps = defaultProps;

const Styled = styled.div`
  background: #fff;
  width: 100%;
  .react-datepicker {
    width: 100%;
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 1rem;
  }
  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container {
    width: 100%;
  }
  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    width: calc(100% / 7);
    margin: 0 auto;
    line-height: 3.5rem;
  }
  input {
    width: 100%;
    color: #000;
    font-size: 16px;
    line-height: 1;
    padding: 12px;
    &[disabled] {
      background-color: ${StyleConfig.COLOR.INPUT_BORDER};
      cursor: not-allowed;
    }
  }
`;

export default MyDatePicker;
